import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'

import StyledBreadcrumb from 'spa/member/components/Breadcrumb'
import { Checkbox } from 'spa/member/components/Checkbox'
import { Select } from 'spa/member/components/Select'
import JobOffersContainer from 'spa/member/components/JobOffersContainer'
import Pagination from 'spa/member/components/Pagination'
import SearchWithOtherQuery from 'spa/member/components/SearchWithOtherQuery'
import JobOfferSearchForm from 'spa/member/components/JobOfferSearchForm'
import NotFoundJobOffers from 'spa/member/components/NotFoundJobOffers'
import { StyledSearchConditionDropdownProps } from 'spa/member/components/JobOfferSearchForm/SearchConditionDropdown/styled'
import { CheckBoxProps } from 'spa/member/components/Checkbox/styled'
import { OptionType as SelectOptionType } from 'spa/member/components/Select/styled'
import SearchingBookmarkSaveModal from 'spa/member/components/SearchingBookmarkSaveModal'
import { SearchWithOtherQueryProps } from 'spa/member/components/SearchWithOtherQuery/styled'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleRight, faSliders } from '@fortawesome/free-solid-svg-icons'
import { SeoContentCategory, SeoContentMasterSkill, UserJobOffer } from 'javascripts/api/generated'
import JobOffersSeoContent from 'spa/member/components/JobOffersSeoContent'
import { UserJobOfferWithInfo } from 'spa/member/types/userJobOfferWithInfo'
import PCRecommendationBanner from 'images/spa/pc_recommended_job_offers_banner.svg'
import SPRecommendationBanner from 'images/spa/sp_recommended_job_offers_banner.svg'

export type StyledJobOffersProps = {
  pageTitle: string,
  word?: string,
  minPrice?: number,
  maxPrice?: number,
  workingDays: CheckBoxProps[],
  workStyles: CheckBoxProps[],
  jobOfferCategories: StyledSearchConditionDropdownProps[],
  prefectures: StyledSearchConditionDropdownProps[],
  skills: StyledSearchConditionDropdownProps[],
  appeals: CheckBoxProps[],
  openonly: boolean,
  categoryIds?: Array<number>,
  prefectureIds?: Array<number>,
  skillIds?: Array<number>,
  appealIds?: Array<number>,
  order?: DisplayOrder,
  page?: number,
  breadcrumbs: string,
  onSearch: (
    word?: string,
    minPrice?: number,
    maxPrice?: number,
    openonly?: boolean,
    workingDayIds?: Array<number>,
    workStyleIds?: Array<number>,
    categoryIds?: Array<number>,
    prefectureIds?: Array<number>,
    skillIds?: Array<number>,
    appealIds?: Array<number>,
    order?: DisplayOrder,
    page?: number,
    options?: { willUpdateDataLayer: boolean }
  ) => void,
  onConditionChange: (
    newOpenonly?: boolean,
    newOrder?: DisplayOrder,
    newPage?: number
  ) => void,
  jobOffers: Array<{ detail: UserJobOffer, isFavorite: boolean, isApplied: boolean }>,
  count: number,
  pageCount: number,
  loggedOut?: boolean,
  searchWithOtherQueryList: Array<SearchWithOtherQueryProps>,
  createUserSearchingHistory: (
    word: string,
    minPrice: number,
    maxPrice: number,
    workingDayIds: Array<number>,
    workStyleIds: Array<number>,
    categoryIds: Array<number>,
    prefectureIds: Array<number>,
    skillIds: Array<number>,
    appealIds: Array<number>,
  ) => void,
  seoContentCategory?: { title: string, seo: SeoContentCategory },
  seoContentMasterSkill?: { title: string, seo: SeoContentMasterSkill },
  selectedCategoryIds?: Array<number>,
  selectedSkillIds?: Array<number>
  selectedPrefectureIds?: Array<number>,
  selectedAppealIds?: Array<number>,
}

export type DisplayOrder = 'newest_first' | 'min_salary_desc' | 'max_salary_desc'

type JobOfferSearchFormValues = {
  word?: string,
  minPrice?: string,
  maxPrice?: string,
  openonly?: boolean,
  workingDayIds?: Array<string>,
  workStyleIds?: Array<string>,
  jobOfferCategoryIds?: Array<string>,
  prefectureIds?: Array<string>,
  skillIds?: Array<string>,
  appealIds?: Array<string>,
  order?: DisplayOrder,
  page?: number
}

const StyledJobOffersPageContent = styled.div`
  display: flex;
  flex-direction: column;

  @media screen and ${props => props.theme.media.sm} {
    flex-direction: column-reverse;
  }
`

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 100%;
  margin: auto;

  @media ${props => props.theme.media.md} {
    padding: 0 1.5rem;
  }
`

const StyledH1 = styled.h1`
  font-family: Noto Sans JP;
  font-weight: 400;
  letter-spacing: 0px;
  text-align: left;
  max-width: 1120px;
  margin: 0;
  padding: 2.25rem 1rem 1rem;
  font-size: 1.25rem;
  line-height: 2rem;

  @media ${props => props.theme.media.md} {
    padding: 4rem 0 1rem;
    font-size: 28px;
    line-height: 48px;
  }
`

const StyledFormContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media ${props => props.theme.media.md} {
    margin: auto;
  }
`

const StyledForm = styled.form`
  position: relative;
  display: flex;
  flex-direction: column-reverse;
  justify-content: center;

  @media ${props => props.theme.media.md} {
    flex-direction: row;
    margin: auto;
  }
`

const StyledJobOffersContainer = styled.div`
  width: auto;
  max-width: 760px;

  @media ${props => props.theme.media.md} {
    margin-right: 40px;
  }
`

const StyledSubInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  margin-top: 16px;
  margin-bottom: 20px;
  padding: 0 1rem;

  @media ${props => props.theme.media.md} {
    flex-direction: row;
    justify-content: space-between;
    padding: 0;
  }
`

const StyledCountText = styled.p`
  font-family: Noto Sans JP;
  font-size: .875rem;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;

  > span {
    font-family: Noto Sans JP;
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: 0px;
    text-align: left;
    margin: auto .25rem .1875rem;

    @media ${props => props.theme.media.md} {
      font-size: 1.75rem;
      margin: auto .25rem .375rem;
    }
  }
`

const StyledDisplayCondition = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: .875rem;
`

const StyledSortBox = styled.div`
  min-width: 198px;
  width: 198px;
  position: relative;

  @media ${props => props.theme.media.md} {
    min-width: 240px;
    width: 240px;
    margin-left: 16px;
  }
`

const StyledPaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 32px;
  margin-bottom: 32px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
`

const StyledOtherQueryBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  margin-bottom: 64px;
  padding: 0 1rem;

  @media ${props => props.theme.media.md} {
    padding: 0;
  }
`

const StyledJobOfferSearchFormContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: scroll;

  @media ${props => props.theme.media.md} {
    position: sticky;
    right: 0;
    top: 0;
    height: 100vh;
    max-width: 320px;
    min-width: 320px;
  }
`

const StyledBannerContainer = styled.div`
  width: 100%;
  margin-top: 16px;

  &:hover {
    opacity: .7;
  }
`

const StyledMenuContainer = styled.div`
  border-bottom: 1px solid ${props => props.theme.members.colors.silver._30};

  @media ${props => props.theme.media.md} {
    border-bottom: none;
  }
`

const StyledUl = styled.ul<{ loggedOut?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: row;
  padding: 0;
  margin-bottom: 17px;

  li {
    flex: 1;
    list-style: none;
    text-decoration: none;
    border: 1px solid ${props => props.theme.members.colors.silver._30};
    border-left: none;
  }

  li:last-child {
    border-right: none;
  }

  @media ${props => props.theme.media.md} {
    flex-direction: column;
    margin-bottom: 32px;

    li {
      width: 100%;
      border: none;
      border-bottom: 1px solid ${props => props.theme.members.colors.silver._30};
    }

    li:first-child {
      border-left: none;
      border-top: ${props => props.loggedOut ?
        `1px solid ${props.theme.members.colors.silver._30}` :
        'none'
      };
    }
  }
`

const StyledLinkBox = styled.a`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 17px 16.5px;
  cursor: pointer;

  p {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: 0px;
    text-decoration: none;
    margin: auto;
    color: ${props => props.theme.members.colors.black._100};

    @media ${props => props.theme.media.md} {
      margin: auto auto auto 0;
    }
  }

  svg {
    position: absolute;
    right: 16.5px;
  }

  &:hover {
    opacity: .7;
    color: ${props => props.theme.members.colors.black};
    text-decoration: none;
  }

  @media ${props => props.theme.media.md} {
    justify-content: space-between;
  }
`

const StyledQueryControlContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 16px;
  margin-bottom: 21px;

  div {
    flex: 1;
    margin: auto;
  }

  @media ${props => props.theme.media.md} {
    margin-top: auto;
    margin-bottom: 16px;
    div {
      flex: none;
      margin: auto 0 auto auto;
    }
  }
`

const StyledSearchFormContainer = styled.div<{ $searchFormOpen: boolean; }>`
  width: 100%;
  position: relative;
`

const StyledSearchConditionButton = styled.button<{ $searchFormOpen?: boolean; }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 14px 32px 14px 32px;
  border-radius: 70px;
  border: 1px solid ${props => props.theme.members.colors.emeraldGreen._40};
  background-color: ${props => props.$searchFormOpen ? props.theme.members.colors.appleGreen._10 : props.theme.members.colors.white};
  margin: auto;

  span {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: 0px;
    text-align: left;
    margin-left: .5rem;
    color: ${props => props.theme.members.colors.black._100};
  }

  @media ${props => props.theme.media.md} {
    display: none;
  }
`

const StyledFontAwesomeIconGreen = styled(FontAwesomeIcon)`
  color: ${props => props.theme.members.colors.emeraldGreen._40};
  font-size: 1.125rem;
  font-weight: 900;
  line-height: 1.4875rem;
`

const StyledQuerySaveLink = styled.div`
  text-align: center;
  margin: auto;

  @media ${props => props.theme.media.md} {
    display: flex;
    margin: auto 0 auto auto;
    justify-content: flex-end;
  }
`

const StyledGreenLink = styled(Link)`
  font-family: Noto Sans JP;
  font-size: 14px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: right;
  cursor: pointer;
  text-decoration: underline;
  color: ${props => props.theme.members.colors.appleGreen._90};

  &:hover {
    opacity: .7;
    color: ${props => props.theme.members.colors.appleGreen._90};
  }
`

const StyledBannerImage = styled.img`
  width: 100%;
  border: 1px solid ${props => props.theme.members.colors.silver._50};
`

export const StyledJobOffers: React.FC<StyledJobOffersProps> = (props: StyledJobOffersProps) => {
  // TODO できれば型ガードを使って、型判定したい
  const getIdsFromOptions = (options: Array<CheckBoxProps>): string[] => {
    return options.filter(item => item.checked || item.defaultChecked).map(item => item.value.toString())
  }

  const workingDayIds = useRef(getIdsFromOptions(props.workingDays))
  const workStyleIds = useRef(getIdsFromOptions(props.workStyles))

  const [latestSubmittedSearchQuery, setLatestSubmittedSearchQuery] = useState<JobOfferSearchFormValues>({
    word: props.word,
    minPrice: String(props.minPrice),
    maxPrice: String(props.maxPrice),
    openonly: props.openonly,
    workingDayIds: workingDayIds.current,
    workStyleIds: workStyleIds.current,
    jobOfferCategoryIds: props.selectedCategoryIds?.map(String),
    prefectureIds: props.selectedPrefectureIds?.map(String),
    skillIds: props.selectedSkillIds?.map(String),
    appealIds: props.selectedAppealIds?.map(String),
    order: props.order,
    page: props.page,
  })
  const methods = useForm<JobOfferSearchFormValues>(
    {
      defaultValues: {
        word: props.word,
        minPrice: String(props.minPrice),
        maxPrice: String(props.maxPrice),
        openonly: props.openonly,
        workingDayIds: workingDayIds.current,
        workStyleIds: workStyleIds.current,
        jobOfferCategoryIds: props.selectedCategoryIds?.map(String),
        prefectureIds: props.selectedPrefectureIds?.map(String),
        skillIds: props.selectedSkillIds?.map(String),
        appealIds: props.selectedAppealIds?.map(String),
        order: props.order,
        page: props.page
      }
    }
  )
  const isPc = !navigator.userAgent.match(/iPhone|Android.+Mobile|iPad/)
  const [searchFormOpen, setSearchFormOpen] = useState<boolean>(isPc)
  const [searchingBookmarkSaveModalOpen, setSearchingBookmarkSaveModalOpen] = useState<boolean>(false)
  const sortOptions: SelectOptionType[] = [
    { label: '新着案件順', value: 'newest_first' },
    { label: '月額単価の上限が高い順', value: 'max_salary_desc' },
    { label: '月額単価の下限が高い順', value: 'min_salary_desc' },
  ]

  const handleOpenOnlyChange = (e: ChangeEvent<HTMLInputElement>) => {
    const openonlyNewState: boolean = e.target.checked
    props.onConditionChange(
      openonlyNewState,
      latestSubmittedSearchQuery.order,
      latestSubmittedSearchQuery.page
    )
    setLatestSubmittedSearchQuery(
      {
        ...latestSubmittedSearchQuery,
        openonly: openonlyNewState
      }
    )
  }
  const handleOrderChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const orderNewState: string = e.target.value
    props.onConditionChange(
      latestSubmittedSearchQuery.openonly,
      orderNewState as DisplayOrder,
      latestSubmittedSearchQuery.page
    )
    setLatestSubmittedSearchQuery(
      {
        ...latestSubmittedSearchQuery,
        order: orderNewState as DisplayOrder
      }
    )
  }
  const handlePageChange = (data: { selected: number }) => {
    props.onConditionChange(
      latestSubmittedSearchQuery.openonly,
      latestSubmittedSearchQuery.order,
      data.selected
    )
    setLatestSubmittedSearchQuery(
      {
        ...latestSubmittedSearchQuery,
        page: data.selected
      }
    )
  }
  const onSubmit = (data: JobOfferSearchFormValues) => {
    props.onSearch(
      data.word,
      data.minPrice ? parseInt(data.minPrice) : undefined,
      data.maxPrice ? parseInt(data.maxPrice) : undefined,
      data.openonly,
      data.workingDayIds?.length ? data.workingDayIds.map(id => parseInt(id)) : undefined,
      data.workStyleIds?.length ? data.workStyleIds.map(id => parseInt(id)) : undefined,
      data.jobOfferCategoryIds?.length ? data.jobOfferCategoryIds.map(id => parseInt(id)) : undefined,
      data.prefectureIds?.length ? data.prefectureIds.map(id => parseInt(id)) : undefined,
      data.skillIds?.length ? data.skillIds.map(id => parseInt(id)) : undefined,
      data.appealIds?.length ? data.appealIds.map(id => parseInt(id)) : undefined,
      data.order,
      undefined,
      { willUpdateDataLayer: true }
    )
    props.createUserSearchingHistory(
      data.word,
      parseInt(data.minPrice),
      parseInt(data.maxPrice),
      data.workingDayIds?.length > 0 ? data.workingDayIds.map(id => parseInt(id)) : [],
      data.workStyleIds?.length > 0 ? data.workStyleIds.map(id => parseInt(id)) : [],
      data.jobOfferCategoryIds?.length > 0 ? data.jobOfferCategoryIds.map(id => parseInt(id)) : [],
      data.prefectureIds?.length > 0 ? data.prefectureIds.map(id => parseInt(id)) : [],
      data.skillIds?.length > 0 ? data.skillIds.map(id => parseInt(id)) : [],
      data.appealIds?.length > 0 ? data.appealIds.map(id => parseInt(id)) : [],
    )
    setLatestSubmittedSearchQuery(data)
    setSearchFormOpen(false)
  }
  const JobOffersRenderContent = (props: { jobOffers: UserJobOfferWithInfo[]; loggedOut: boolean; pageCount: number; page: number; count: number }) => {
    if (props.jobOffers?.length > 0) {
      return (
        <>
          <JobOffersContainer jobOffers={props.jobOffers} loggedOut={props.loggedOut} />
          <StyledPaginationContainer>
            <Pagination handlePageChange={handlePageChange} pageCount={props.pageCount} initialPage={props.page} />
          </StyledPaginationContainer>
        </>
      )
    } else if (Number(props.count) === 0) {
      return <NotFoundJobOffers />
    } else {
      return <NotFoundJobOffers />
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [props.onSearch])

  useEffect(() => {
    workingDayIds.current = getIdsFromOptions(props.workingDays)
  }, [props.workingDays])

  useEffect(() => {
    workStyleIds.current = getIdsFromOptions(props.workStyles)
  }, [props.workStyles])

  return (
    <StyledJobOffersPageContent>
      <StyledContainer>
        <FormProvider {...methods}>
          <StyledFormContainer>
            <StyledH1 data-cy="job-offers-header-title">{props.pageTitle}</StyledH1>
            <StyledForm onSubmit={methods.handleSubmit(onSubmit)}>
              <StyledJobOffersContainer>
                <StyledSubInfo>
                  <StyledCountText>該当するお仕事数<span data-cy="job-offers-header-count">{props.count}</span>件</StyledCountText>
                  <StyledDisplayCondition data-cy="job-offers-display-condition">
                    <Checkbox name='openonly' label='募集中のみ表示' defaultChecked={props.openonly} onChange={(e) => handleOpenOnlyChange(e)} />
                    <StyledSortBox>
                      <Select name='order' options={sortOptions} defaultValue={props.order} onChange={(e) => handleOrderChange(e)} inputSize="small" />
                    </StyledSortBox>
                  </StyledDisplayCondition>
                  {!props.loggedOut && !isPc && (
                    <StyledBannerContainer data-cy="recommended-job-offers-banner">
                      <Link to='/recommended_job_offers'>
                        <StyledBannerImage
                          src={SPRecommendationBanner}
                          alt='あなたにおすすめの案件特集'
                        />
                      </Link>
                    </StyledBannerContainer>
                  )}
                </StyledSubInfo>
                <JobOffersRenderContent {...props} />
                <StyledOtherQueryBox>
                  {props.searchWithOtherQueryList.map((item, index) => {
                    if (item) return (
                      <SearchWithOtherQuery key={index} title={item.title} category_abbreviation={item.category_abbreviation} queryItems={item.queryItems} />
                    )
                  })}
                </StyledOtherQueryBox>
                {props.seoContentCategory && (
                  <JobOffersSeoContent
                    title={props.seoContentCategory.title}
                    aboutCategory={props.seoContentCategory.seo.about}
                    requiredSkill={props.seoContentCategory.seo.required_skill}
                    relatedArticles={props.seoContentCategory.seo.articles}
                  />
                )}
                {props.seoContentMasterSkill && (
                  <JobOffersSeoContent
                    title={props.seoContentMasterSkill.title}
                    aboutSkill={props.seoContentMasterSkill.seo.about}
                    relatedArticles={props.seoContentMasterSkill.seo.articles}
                  />
                )}
              </StyledJobOffersContainer>
              <StyledJobOfferSearchFormContainer>
                {!props.loggedOut && isPc && (
                  <StyledBannerContainer data-cy="recommended-job-offers-banner">
                    <Link to='/recommended_job_offers'>
                      <StyledBannerImage
                        src={PCRecommendationBanner}
                        alt='あなたにおすすめの案件特集'
                      />
                    </Link>
                  </StyledBannerContainer>
                )}
                <StyledMenuContainer>
                  <StyledUl loggedOut={props.loggedOut}>
                    <li>
                      <StyledLinkBox href={'/favorite_job_offers'} data-cy='favorite-job-offers-link'>
                        <p>気になったお仕事</p>
                        <FontAwesomeIcon icon={faAngleRight} color='silver' />
                      </StyledLinkBox>
                    </li>
                    <li>
                      <StyledLinkBox href={'/searching_bookmarks'} data-cy='job-offers-searching-bookmarks-link'>
                        <p>保存した検索条件</p>
                        <FontAwesomeIcon icon={faAngleRight} color='silver' />
                      </StyledLinkBox>
                    </li>
                  </StyledUl>
                  <StyledQueryControlContainer>
                    <div>
                      {/* TODO 表示時のアニメーションをつける */}
                      <StyledSearchConditionButton type='button' $searchFormOpen={searchFormOpen} onClick={() => setSearchFormOpen(!searchFormOpen)}>
                        <StyledFontAwesomeIconGreen icon={faSliders} />
                        <span>条件検索</span>
                      </StyledSearchConditionButton>
                    </div>
                    {!props.loggedOut && (
                      <div>
                        <StyledQuerySaveLink>
                          <StyledGreenLink onClick={(e) => {
                            e.preventDefault()
                            setSearchingBookmarkSaveModalOpen(true)
                          }}>
                            現在の検索条件を保存する
                          </StyledGreenLink>
                        </StyledQuerySaveLink>
                      </div>
                    )}
                  </StyledQueryControlContainer>
                </StyledMenuContainer>
                {(isPc || searchFormOpen) && (
                  <StyledSearchFormContainer $searchFormOpen={searchFormOpen}>
                    <JobOfferSearchForm word={props.word} minPrice={props.minPrice} maxPrice={props.maxPrice} workingDays={props.workingDays} workStyles={props.workStyles} jobOfferCategories={props.jobOfferCategories} prefectures={props.prefectures} skills={props.skills} appeals={props.appeals} count={props.count} selectedCategoryIds={props.selectedCategoryIds || []} selectedSkillIds={props.selectedSkillIds || []} selectedPrefectureIds={props.selectedPrefectureIds || []} selectedAppealIds={props.selectedAppealIds || []} />
                  </StyledSearchFormContainer>
                )}
              </StyledJobOfferSearchFormContainer>
            </StyledForm>
          </StyledFormContainer>
        </FormProvider>
        {searchingBookmarkSaveModalOpen &&
          <SearchingBookmarkSaveModal
            word={latestSubmittedSearchQuery.word}
            minPrice={latestSubmittedSearchQuery.minPrice}
            maxPrice={latestSubmittedSearchQuery.maxPrice}
            workingDays={workingDayIds.current?.length ? workingDayIds.current : undefined}
            workStyles={workStyleIds.current?.length ? workStyleIds.current : undefined}
            categoryIds={latestSubmittedSearchQuery.jobOfferCategoryIds?.length ? latestSubmittedSearchQuery.jobOfferCategoryIds.map(id => parseInt(id)) : undefined}
            prefectureIds={latestSubmittedSearchQuery.prefectureIds?.length ? latestSubmittedSearchQuery.prefectureIds.map(id => parseInt(id)) : undefined}
            skillIds={latestSubmittedSearchQuery.skillIds?.length ? latestSubmittedSearchQuery.skillIds.map(id => parseInt(id)) : undefined}
            appealIds={latestSubmittedSearchQuery.appealIds?.length ? latestSubmittedSearchQuery.appealIds.map(id => parseInt(id)) : undefined}
            setIsModalOpenFalse={() => setSearchingBookmarkSaveModalOpen(false)}
          />
        }
      </StyledContainer>
      <StyledBreadcrumb breadcrumb={props.breadcrumbs} />
    </StyledJobOffersPageContent>
  )
}
